import * as React from 'react';
import Box from '@mui/joy/Box';
import Button from '@mui/joy/Button';
import Container from '@mui/joy/Container';
import Divider from '@mui/joy/Divider';
import Typography from '@mui/joy/Typography';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import company from '../company';
import { navigate, routes, RouterLink } from '../router';

/** Um item de lista: texto simples ou um par rótulo/descrição. */
export type LegalItem = string | { label: string; text: string };

/** Um bloco é um parágrafo (string) ou uma lista de itens (array). */
export type LegalBlock = string | LegalItem[];

export type LegalSection = {
  title: string;
  blocks: LegalBlock[];
};

function Paragraph({ children }: React.PropsWithChildren<{}>) {
  return (
    <Typography textColor="text.secondary" lineHeight="lg" sx={{ mt: 1.5 }}>
      {children}
    </Typography>
  );
}

function BulletList({ items }: { items: LegalItem[] }) {
  return (
    <Box component="ul" sx={{ my: 1.5, pl: 3, display: 'grid', gap: 1 }}>
      {items.map((item, index) => (
        <Typography
          key={index}
          component="li"
          textColor="text.secondary"
          lineHeight="lg"
        >
          {typeof item === 'string' ? (
            item
          ) : (
            <React.Fragment>
              <Typography fontWeight="lg" textColor="text.primary">
                {item.label}:
              </Typography>{' '}
              {item.text}
            </React.Fragment>
          )}
        </Typography>
      ))}
    </Box>
  );
}

type LegalPageProps = {
  title: string;
  documentTitle: string;
  intro: string;
  sections: LegalSection[];
};

export default function LegalPage({
  title,
  documentTitle,
  intro,
  sections,
}: LegalPageProps) {
  React.useEffect(() => {
    const previous = document.title;
    document.title = documentTitle;
    return () => {
      document.title = previous;
    };
  }, [documentTitle]);

  return (
    <Container sx={{ maxWidth: '72ch !important', py: { xs: 6, md: 10 } }}>
      <Button
        variant="plain"
        color="neutral"
        size="sm"
        startDecorator={<ArrowBackRoundedIcon />}
        onClick={() => navigate(routes.home)}
        sx={{ ml: -1, mb: 3 }}
      >
        Voltar para o início
      </Button>

      <Typography level="h1" fontWeight="xl" fontSize="clamp(1.75rem, 1.4rem + 1.6vw, 2.5rem)">
        {title}
      </Typography>
      <Typography level="body-sm" textColor="text.tertiary" sx={{ mt: 1 }}>
        Última atualização: {company.lastUpdated}
      </Typography>

      <Paragraph>{intro}</Paragraph>

      {sections.map((section, sectionIndex) => (
        <Box key={section.title} component="section" sx={{ mt: 5 }}>
          <Typography level="h3" fontSize="xl" fontWeight="lg">
            {sectionIndex + 1}. {section.title}
          </Typography>
          {section.blocks.map((block, blockIndex) =>
            Array.isArray(block) ? (
              <BulletList key={blockIndex} items={block} />
            ) : (
              <Paragraph key={blockIndex}>{block}</Paragraph>
            ),
          )}
        </Box>
      ))}

      <Divider sx={{ my: 5 }} />

      <Typography level="body-sm" textColor="text.tertiary">
        {company.name} — CNPJ {company.cnpj} — Contato: {company.phone}
      </Typography>
      <Typography level="body-sm" sx={{ mt: 1 }}>
        <RouterLink to={routes.privacy}>Política de Privacidade</RouterLink>
        {' · '}
        <RouterLink to={routes.terms}>Termos de Uso</RouterLink>
      </Typography>
    </Container>
  );
}
